import { makeAutoObservable } from 'mobx';
import React from 'react';
import { AccountInterface } from 'type/accountType';
import { accountApiService } from '../service/api/accountApiService';
import {
  ADMIN_MENU_ITEMS,
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  CP_MENU_KEY_LIST,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  PERMISSION_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
  TOOLS_MENU_ITEMS,
  VENDOR_MENU_ITEMS,
  VIETNAM_BASE_MENU_KEY_LIST,
} from 'constants/menu';
import { isVietnamPlatform } from 'constants/constant';
import {
  VIETNAM_PERMISSION_PREFIX,
  VIETNAM_UPLOADS_ADMIN_MENU,
  VIETNAM_UPLOADS_MENU,
} from 'constants/permission';

const SUPER_ADMIN_USER_ID = '1';

const collectMenuKeys = (items: { key?: React.Key }[] | undefined): string[] =>
  (items ?? [])
    .map(item => (typeof item?.key === 'string' ? item.key : ''))
    .filter((k): k is string => !!k);

const ALL_MENU_KEYS: string[] = [
  ...collectMenuKeys(CONTENT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(META_DATA_STRUCTURE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(CONTRACT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(REVENUE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(INGEST_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(SYNC_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(TOOLS_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(VENDOR_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(ADMIN_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(PERMISSION_MENU_ITEMS as { key?: React.Key }[] | undefined),
];

class AccountStore {
  private account: AccountInterface | null = null;
  private projectId: number | null = null;
  private role: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public reset = (): void => {
    this.account = null;
    this.projectId = null;
    this.role = null;
  };

  public async init(): Promise<void> {
    const account = await accountApiService.fetchAccount();
    this.account = account;
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(this.account?.id)
    );
    this.projectId = projectId;
    this.role = role;
  }

  public async fetchAccount(): Promise<AccountInterface> {
    if (this.account) {
      return this.account;
    }
    const account = await accountApiService.fetchAccount();
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(account.id)
    );
    this.projectId = projectId;
    this.role = role;
    this.account = account;
    return account;
  }

  public async update(account: AccountInterface): Promise<void> {
    await accountApiService.update(Number(account.id), account);
  }

  public async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    await accountApiService.updatePassword(oldPassword, newPassword);
  }

  public setAccount = (account: AccountInterface): void => {
    this.account = account;
  };

  public setProject = (projectId: number): void => {
    if (this.projectId) {
      this.projectId = projectId;
    } else {
      throw new Error('Account not found');
    }
  };

  public getAccount = (): AccountInterface | null => {
    return this.account;
  };

  public getProject = (): number | null => {
    return this.projectId;
  };

  public getRole = (): string | null => {
    return this.role;
  };

  public isStaff = (): boolean => {
    return this.account?.is_staff ?? false;
  };

  public isInit = (): boolean => {
    return this.account !== null;
  };

  public isSuperAdmin = (): boolean => {
    return String(this.account?.id ?? '') === SUPER_ADMIN_USER_ID;
  };

  public getMenus = (): string[] => {
    // Super admin (user_id=1): 권한과 무관하게 모든 메뉴 노출.
    // 단, vendor-download는 벤더 사용자 전용 다운로드 페이지이므로 super admin에게도 숨김.
    if (this.isSuperAdmin()) {
      return ALL_MENU_KEYS.filter(k => k !== 'vendor-download');
    }

    const permissions = Array.from(this.account?.permissions ?? []);
    const permissionList = permissions.map(permission => permission.split(':')[0]);

    // Vendor-only 사용자(vendor-* 권한만 보유)는 INGEST/SYNC auto-push 제외하고
    // 본인의 vendor 메뉴만 노출.
    const isVendorOnly =
      permissionList.length > 0 && permissionList.every(key => key.startsWith('vendor-'));
    if (isVendorOnly) {
      return permissionList;
    }

    // Vietnam 전용 계정(vietnam-* 권한만 보유): INGEST/SYNC auto-push를 건너뛰고
    // 공통 메뉴 + 본인이 보유한 vietnam-* 메뉴만 노출.
    if (this.isVietnamOnly()) {
      const vietnamMenus = permissionList.filter(key => key.startsWith(VIETNAM_PERMISSION_PREFIX));
      // platform fallback 경로에서는 vietnam-* 권한이 아직 없으므로 기존 고정 메뉴를 보장한다.
      const resolved = vietnamMenus.length > 0 ? vietnamMenus : [VIETNAM_UPLOADS_MENU];
      return Array.from(new Set([...VIETNAM_BASE_MENU_KEY_LIST, ...resolved]));
    }

    permissionList.push(...INGEST_MENU_ITEMS!.map(item => item!.key as string));
    permissionList.push(...SYNC_MENU_ITEMS!.map(item => item!.key as string));
    return this.account?.platform.id === 2
      ? permissionList.filter(item => CP_MENU_KEY_LIST.includes(item))
      : permissionList;
  };

  public getPermissions = (): string[] => {
    return this.account?.permissions ?? [];
  };

  public hasPermission = (menu: string, action: string): boolean => {
    // Super admin은 모든 권한 통과.
    if (this.isSuperAdmin()) return true;
    const permissions = Array.from(this.account?.permissions ?? []);
    const permission = `${menu}:${action}`;
    return permissions.includes(permission);
  };

  // --- Vietnam 업로드 접근 규칙 (constants/permission.ts 참고) --------------------
  //
  // MIGRATION FALLBACK: BE Permission 테이블에 vietnam-* code가 등록되기 전까지는
  // 기존 platform 판정을 함께 인정해 현재 Vietnam CP가 잠기지 않게 한다.
  // BE Role 세팅이 끝나면 아래 `|| isVietnamPlatform(...)` 세 군데와
  // constants/constant.ts의 VIETNAM_PLATFORM_IDS/isVietnamPlatform을 제거하면 된다.

  private isVietnamPlatformAccount = (): boolean => isVietnamPlatform(this.account?.platform.id);

  /**
   * Vietnam 전용 계정 = 보유 권한이 전부 `vietnam-*` (vendor-only 판정과 동형).
   * 이 계정만 `/assets/add` 진입 시 VN 업로드 화면으로 자동 전환된다.
   * Super admin은 모든 화면을 봐야 하므로 해당 없음.
   */
  public isVietnamOnly = (): boolean => {
    if (this.isSuperAdmin()) return false;
    const menuKeys = (this.account?.permissions ?? []).map(p => p.split(':')[0]);
    const isPrefixOnly =
      menuKeys.length > 0 && menuKeys.every(key => key.startsWith(VIETNAM_PERMISSION_PREFIX));
    return isPrefixOnly || this.isVietnamPlatformAccount();
  };

  /** 자(CP): 본인 회사 업로드 목록 (`/vietnam-uploads`). */
  public canReadOwnVietnamUploads = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, 'read') || this.isVietnamPlatformAccount();

  /** VN 전용 Direct Upload 화면 (`/assets/add-vn`). */
  public canAddVietnamUpload = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, 'add') || this.isVietnamPlatformAccount();

  /**
   * 모(CP)/admin: 전 회사 업로드 목록 (`/vietnam-uploads-admin`).
   * 신규 capability라 platform fallback 없음 — permission 없으면 접근 불가.
   */
  public canReadAllVietnamUploads = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_ADMIN_MENU, 'read');
}

export const accountStore = new AccountStore();
